import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { authAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
    KeyRound, 
    Loader2, 
    CheckCircle2, 
    XCircle, 
    Eye, 
    EyeOff,
    ArrowLeft,
    ShieldCheck,
    AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenError, setTokenError] = useState('');
    const [email, setEmail] = useState('');
    const [success, setSuccess] = useState(false);
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Password validation
    const passwordRequirements = [
        { label: 'At least 8 characters', valid: password.length >= 8 },
        { label: 'Contains a number', valid: /\d/.test(password) },
        { label: 'Contains uppercase letter', valid: /[A-Z]/.test(password) },
        { label: 'Passwords match', valid: password === confirmPassword && confirmPassword.length > 0 },
    ];
    
    const allRequirementsMet = passwordRequirements.every(r => r.valid);

    // Verify token on mount
    useEffect(() => {
        if (!token) {
            setVerifying(false);
            setTokenError('No reset token provided');
            return;
        }

        const verifyToken = async () => {
            try {
                const res = await authAPI.verifyResetToken(token);
                if (res.data?.success || res.data?.data?.valid) {
                    setTokenValid(true);
                    setEmail(res.data?.data?.email || res.data?.email || '');
                } else {
                    setTokenError('Invalid or expired reset token');
                }
            } catch (error) {
                const errorMsg = error.response?.data?.message || 'Invalid or expired reset token';
                setTokenError(errorMsg);
            } finally {
                setVerifying(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!allRequirementsMet) {
            toast.error('Please meet all password requirements');
            return;
        }

        setLoading(true);
        try {
            await authAPI.resetPassword({ token, password });
            setSuccess(true);
            toast.success('Password reset successfully!');
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to reset password';
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Loading state
    if (verifying) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
                        <p className="text-muted-foreground">Verifying reset link...</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Invalid token state
    if (!tokenValid && !verifying) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center space-y-4">
                        <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                            <XCircle className="w-8 h-8 text-red-600" />
                        </div>
                        <CardTitle className="text-2xl">Invalid Reset Link</CardTitle>
                        <CardDescription>
                            {tokenError || 'This password reset link is invalid or has expired.'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                Password reset links expire after 1 hour for security reasons. 
                                Please request a new reset link.
                            </AlertDescription>
                        </Alert>
                        
                        <div className="flex flex-col gap-2">
                            <Button asChild className="w-full">
                                <Link to="/forgot-password">
                                    Request New Reset Link
                                </Link>
                            </Button>
                            <Button variant="outline" asChild className="w-full">
                                <Link to="/login">
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Back to Login
                                </Link>
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center space-y-4">
                        <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle2 className="w-8 h-8 text-green-600" />
                        </div>
                        <CardTitle className="text-2xl">Password Reset Successful!</CardTitle>
                        <CardDescription>
                            Your password has been reset successfully. You can now log in with your new password.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button asChild className="w-full" size="lg">
                            <Link to="/login">
                                Continue to Login
                            </Link>
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Reset password form
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center space-y-4">
                    <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <KeyRound className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                        <CardTitle className="text-2xl">Reset Your Password</CardTitle>
                        <CardDescription className="mt-2">
                            {email ? (
                                <>Create a new password for <strong>{email}</strong></>
                            ) : (
                                'Create a new secure password for your account'
                            )}
                        </CardDescription>
                    </div>
                </CardHeader>
                
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* New Password */}
                        <div className="space-y-2">
                            <Label htmlFor="password">New Password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    className="pr-10"
                                    autoComplete="new-password"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-muted-foreground" />
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirm New Password</Label>
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    className="pr-10"
                                    autoComplete="new-password"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-muted-foreground" />
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Password Requirements */}
                        <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                            <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4" />
                                Password Requirements
                            </p>
                            <ul className="space-y-1">
                                {passwordRequirements.map((req, index) => (
                                    <li key={index} className="flex items-center gap-2 text-sm">
                                        {req.valid ? (
                                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                                        ) : (
                                            <XCircle className="w-4 h-4 text-muted-foreground" />
                                        )}
                                        <span className={cn(
                                            req.valid ? "text-green-700" : "text-muted-foreground"
                                        )}>
                                            {req.label}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Submit Button */}
                        <Button 
                            type="submit" 
                            className="w-full" 
                            size="lg"
                            disabled={loading || !allRequirementsMet}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Resetting Password...
                                </>
                            ) : (
                                <>
                                    <KeyRound className="w-4 h-4 mr-2" />
                                    Reset Password
                                </>
                            )}
                        </Button>

                        {/* Back to login */}
                        <div className="text-center">
                            <Link 
                                to="/login" 
                                className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
                            >
                                <ArrowLeft className="w-3 h-3" />
                                Back to Login
                            </Link>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}